import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import Breadcrumb from '../../components/navigation/Breadcrumb';
import NotificationCard from './components/NotificationCard';
import NotificationFilters from './components/NotificationFilters';
import LiveUpdateCard from './components/LiveUpdateCard';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import PushNotificationSettings from './components/PushNotificationSettings';
import {
  subscribeToUserLeaderboardChanges,
  subscribeToTournamentUpdates,
  unsubscribeChannels
} from '../../services/realtimeService';


const Notifications = () => {
  const { user } = useAuth();
  const [activeFilter, setActiveFilter] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [liveUpdates, setLiveUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [realtimeChannels, setRealtimeChannels] = useState([]);

  // Fetch notifications from Supabase
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    fetchNotifications();
  }, [user]);

  // Add this function before it's used
  const convertToCamelCase = (notification) => ({
    id: notification?.id,
    userId: notification?.user_id,
    type: notification?.type,
    title: notification?.title,
    message: notification?.message,
    isRead: notification?.is_read,
    link: notification?.link,
    metadata: notification?.metadata,
    createdAt: notification?.created_at,
    updatedAt: notification?.updated_at
  });

  // Real-time subscription for instant updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase?.channel('notifications_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user?.id}`
        },
        (payload) => {
          if (payload?.eventType === 'INSERT') {
            setNotifications((prev) => [convertToCamelCase(payload?.new), ...prev]);
          } else if (payload?.eventType === 'UPDATE') {
            setNotifications((prev) =>
              prev?.map((notif) =>
                notif?.id === payload?.new?.id ? convertToCamelCase(payload?.new) : notif
              )
            );
          } else if (payload?.eventType === 'DELETE') {
            setNotifications((prev) =>
              prev?.filter((notif) => notif?.id !== payload?.old?.id)
            );
          }
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [user]);

  // WebSocket subscriptions for live tournament updates
  useEffect(() => {
    if (!user) return;

    const channels = [];

    // Subscribe to user's leaderboard changes across all tournaments
    const leaderboardChannel = subscribeToUserLeaderboardChanges(
      user?.id,
      (update) => {
        // Create live update for rank changes
        if (update?.rankChange !== 0) {
          const liveUpdate = {
            id: `live_${Date.now()}_${Math.random()}`,
            type: 'rank_change',
            title: 'Leaderboard Position Changed',
            message: `You moved ${update?.rankChange > 0 ? 'up' : 'down'} ${Math.abs(update?.rankChange)} position${Math.abs(update?.rankChange) > 1 ? 's' : ''}! You are now ranked #${update?.newRank}.`,
            oldRank: update?.oldRank,
            newRank: update?.newRank,
            rankChange: update?.rankChange,
            timestamp: update?.timestamp
          };
          setLiveUpdates((prev) => [liveUpdate, ...prev]?.slice(0, 10)); // Keep last 10 updates
        }

        // Create live update for points changes
        if (update?.pointsGained !== 0) {
          const liveUpdate = {
            id: `live_${Date.now()}_${Math.random()}`,
            type: 'points_update',
            title: 'Points Updated',
            message: `You earned ${update?.pointsGained} points! Total: ${update?.newPoints} points.`,
            oldPoints: update?.oldPoints,
            newPoints: update?.newPoints,
            pointsGained: update?.pointsGained,
            rank: update?.newRank,
            timestamp: update?.timestamp
          };
          setLiveUpdates((prev) => [liveUpdate, ...prev]?.slice(0, 10));
        }
      }
    );
    channels?.push(leaderboardChannel);

    // Subscribe to tournament updates
    const tournamentChannel = subscribeToTournamentUpdates((update) => {
      if (update?.type === 'new_tournament') {
        const liveUpdate = {
          id: `live_${Date.now()}_${Math.random()}`,
          type: 'tournament_update',
          title: 'New Tournament Available',
          message: `${update?.tournamentName} is now open for registration!`,
          timestamp: update?.timestamp
        };
        setLiveUpdates((prev) => [liveUpdate, ...prev]?.slice(0, 10));
      } else if (update?.type === 'status_change' && update?.newStatus === 'active') {
        const liveUpdate = {
          id: `live_${Date.now()}_${Math.random()}`,
          type: 'tournament_update',
          title: 'Tournament Started',
          message: `${update?.tournamentName} has started! Make your predictions now.`,
          timestamp: update?.timestamp
        };
        setLiveUpdates((prev) => [liveUpdate, ...prev]?.slice(0, 10));
      }
    });
    channels?.push(tournamentChannel);

    setRealtimeChannels(channels);

    // Cleanup subscriptions
    return () => {
      unsubscribeChannels(channels);
    };
  }, [user]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase?.from('notifications')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setNotifications(data?.map(convertToCamelCase) || []);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const { error: updateError } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', notificationId)?.eq('user_id', user?.id);

      if (updateError) throw updateError;

      // Optimistic update
      setNotifications((prev) =>
        prev?.map((notif) =>
          notif?.id === notificationId ? { ...notif, isRead: true } : notif
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      const { error: deleteError } = await supabase?.from('notifications')?.delete()?.eq('id', notificationId)?.eq('user_id', user?.id);

      if (deleteError) throw deleteError;

      // Optimistic update
      setNotifications((prev) => prev?.filter((notif) => notif?.id !== notificationId));
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const { error: updateError } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('user_id', user?.id)?.eq('is_read', false);

      if (updateError) throw updateError;

      // Optimistic update
      setNotifications((prev) => prev?.map((notif) => ({ ...notif, isRead: true })));
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleClearAll = async () => {
    try {
      const { error: deleteError } = await supabase?.from('notifications')?.delete()?.eq('user_id', user?.id);

      if (deleteError) throw deleteError;

      // Optimistic update
      setNotifications([]);
    } catch (err) {
      console.error('Error clearing all notifications:', err);
    }
  };

  const handleDismissLiveUpdate = (updateId) => {
    setLiveUpdates((prev) => prev?.filter((update) => update?.id !== updateId));
  };

  const filteredNotifications = notifications?.filter((notif) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'unread') return !notif?.isRead;
    return notif?.type === activeFilter;
  });

  const unreadCount = notifications?.filter((notif) => !notif?.isRead)?.length;

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/user-dashboard' },
    { label: 'Notifications', path: '/notifications' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-20 pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-20 pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-center">
              <p className="text-destructive font-medium">Error loading notifications</p>
              <p className="text-sm text-muted-foreground mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={breadcrumbItems} />

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Notifications
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Stay updated with match deadlines, results, and leaderboard changes
              </p>
            </div>

            {notifications?.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleMarkAllAsRead}
                  className="px-3 py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Icon name="CheckCheck" size={16} />
                  Mark All Read
                </button>
                <button
                  onClick={handleClearAll}
                  className="px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Icon name="Trash2" size={16} />
                  Clear All
                </button>
              </div>
            )}
          </div>

          {/* Filters */}
          <NotificationFilters
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            unreadCount={unreadCount}
            notifications={notifications}
          />

          {/* Push Notification Settings */}
          <div className="mb-6">
            <PushNotificationSettings />
          </div>

          {/* Live Updates Section */}
          {liveUpdates?.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="Zap" size={20} className="text-primary" />
                <h2 className="text-lg font-semibold text-foreground">Live Updates</h2>
                <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  {liveUpdates?.length}
                </span>
              </div>
              <div className="space-y-3">
                {liveUpdates?.map((update) => (
                  <LiveUpdateCard
                    key={update?.id}
                    update={update}
                    onDismiss={handleDismissLiveUpdate}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Notifications List */}
          <div className="space-y-4">
            {filteredNotifications?.length > 0 ? (
              filteredNotifications?.map((notification) => (
                <NotificationCard
                  key={notification?.id}
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  onDelete={handleDelete}
                />
              ))
            ) : (
              <div className="bg-card rounded-lg shadow-elevation-sm p-8 md:p-12 text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Bell" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">
                  No notifications
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  {activeFilter === 'unread' ? "You're all caught up! No unread notifications."
                    : activeFilter === 'all'
                    ? "You don't have any notifications yet."
                    : `No ${activeFilter} notifications at the moment.`}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;